import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("nyla_admin_token")}` });

// Extracts a Google-Drive file ID from any of the common share/link formats.
const driveFileId = (url) => {
  if (!url) return null;
  const m =
    url.match(/drive\.google\.com\/file\/d\/([\w-]+)/) ||
    url.match(/drive\.google\.com\/open\?id=([\w-]+)/) ||
    url.match(/drive\.google\.com\/uc\?[^\s]*id=([\w-]+)/) ||
    url.match(/lh3\.googleusercontent\.com\/d\/([\w-]+)/);
  return m ? m[1] : null;
};

/**
 * Convert any Google-Drive share URL into a direct-media URL appropriate for
 * the target element:
 *   - kind="image"  → https://lh3.googleusercontent.com/d/{ID}   (thumbnail/CDN)
 *   - kind="video"  → https://drive.google.com/uc?export=download&id={ID}
 * Non-Drive URLs are returned unchanged.
 */
export const normalizeMediaUrl = (url, kind = "image") => {
  if (!url) return url;
  const trimmed = url.trim();
  const id = driveFileId(trimmed);
  if (!id) return trimmed;
  return kind === "video"
    ? `https://drive.google.com/uc?export=download&id=${id}`
    : `https://lh3.googleusercontent.com/d/${id}`;
};

// Backwards-compatible alias — previous callers imported this name.
export const normalizeImageUrl = (url) => normalizeMediaUrl(url, "image");

export const ImageField = ({ value, onChange, testId, kind = "image", aiPrompt }) => {
  const [converted, setConverted] = useState(false);
  const [generating, setGenerating] = useState(false);

  const handleChange = (raw) => {
    const normalized = normalizeMediaUrl(raw, kind);
    setConverted(normalized !== raw.trim() && raw.trim().length > 0);
    onChange(normalized);
  };

  const generateWithAi = async () => {
    const prompt = typeof aiPrompt === "function" ? aiPrompt() : aiPrompt;
    if (!prompt || !prompt.trim()) {
      toast.error("Add a title first so the image knows what to depict");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/ai/generate-image`,
        { prompt },
        { headers: authHeaders(), timeout: 120000 }
      );
      onChange(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success("Cover image generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Image generation failed — try again");
    } finally {
      setGenerating(false);
    }
  };

  const placeholder =
    kind === "video"
      ? "Paste video URL (Google Drive share links work too)"
      : "Paste image URL (Google Drive share links work too)";

  const hint = converted
    ? `✓ Google Drive link converted to a direct ${kind} URL`
    : `Tip: paste a Google Drive share link (set to 'Anyone with the link') and it converts automatically.`;

  return (
    <div>
      <div className="flex items-start gap-2">
        <input
          data-testid={testId}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-[#F5F5F7] border border-[#D2D2D7] rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#2C3E35]"
        />
        {aiPrompt !== undefined && kind === "image" && (
          <button
            type="button"
            data-testid={testId ? `${testId}-ai-generate` : "image-ai-generate"}
            onClick={generateWithAi}
            disabled={generating}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-2xl bg-[#1D1D1F] text-white text-xs font-medium hover:bg-black disabled:opacity-60 whitespace-nowrap transition-colors duration-200"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {generating ? "Creating…" : "Generate with AI"}
          </button>
        )}
      </div>
      {generating && (
        <p className="mt-1.5 text-[11px] text-[#2C3E35]">Crafting an original cover — this can take up to a minute…</p>
      )}
      <p className="mt-1.5 text-[11px] text-[#86868B]">{hint}</p>
      {kind === "video" && (
        <p className="mt-1 text-[11px] text-[#86868B]/80">
          Note: Google Drive can throttle files above ~25 MB. For a smooth hero
          loop, prefer a self-hosted or CDN-hosted MP4 under 8 MB.
        </p>
      )}
    </div>
  );
};
